import { useState, useCallback, useMemo, useEffect } from 'react';
import { createApiClient } from '../../../api/poshwatch_client.ts';
import { CursorPaginationInfo } from '../../../../libs/poshwatch_ts_client/api';

// Configuration constants
const DEFAULT_PAGE_SIZE = 20;

// Default pagination state for resetting
const DEFAULT_PAGINATION: CursorPaginationInfo = {
  limit: DEFAULT_PAGE_SIZE,
  hasMore: false,
  nextCursor: undefined,
  currentCursor: undefined,
};

export const useTrending = (): any => {
  // Memoize the styles array to prevent recreation on every render
  const styles = useMemo(() => [
    "Lilly Pulitzer",
    "For Love And Lemons",
    "Spanx",
    "ba&sh",
    "AYR",
    "Agolde",
    "Gola",
    "Coach",
    "Tory Birch",
    "Pistola",
    "rag & bone",
    "Mac Duggal",
    "Rat & Boa",
    "KHAITE",
    "Alice + Olivia",
    "Good American",
    "Kate Spade",
    "Sandro",
    "Frame",
    "Madewell",
    "Skims",
    "Vuori",
    "Anthropologie",
    "House of CB",
    "Reformation",
    "Birkenstock",
    "Frye",
    "Crocs",
    "Rothys",
    "Old Navy",
    "Aritzia",
    "Levi's",
    "Alemais",
    "J. Crew",
    "Adidas",
    "Line & Dot",
    "Jil Sander",
    "Isabel Marant",
    "Ulla Johnson",
    "Matteau",
    "Eileen Fisher",
    "Hoka",
    "Everlane",
    "Aje",
    "Citizen of Humanity",
    "Figs",
    "Patagonia",
    "Alo",
    "Alohas",
  ].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())), []);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [tempSelectedBrands, setTempSelectedBrands] = useState<string[]>([]);
  
  // Cursor-based pagination state
  const [pagination, setPagination] = useState<CursorPaginationInfo>(DEFAULT_PAGINATION);
  // Track cursor history for Previous button
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);

  const fetchTrends = useCallback(async (brands: string[], limit: number = DEFAULT_PAGE_SIZE, cursor?: string, isNextPage: boolean = false) => {
    try {
      setIsLoading(true);
      setError(null);

      const client = await createApiClient();
      
      // Pass the brands array to the API
      // Empty array will be treated as "all brands" by the API
      const response = await client.getTrendingStyles(brands, limit, cursor);
        
      const results = response.data.results || [];
      const paginationData = response.data.pagination as CursorPaginationInfo;

      // Update cursor history for Previous button
      if (isNextPage && cursor) {
        setCursorHistory(prev => [...prev, cursor]);
      }

      setSearchResults(results);
      const updatedPagination = {
        ...(paginationData as CursorPaginationInfo),
        currentCursor: cursor || undefined
      };
      setPagination(updatedPagination);
    } catch (err) {
      console.error('Error searching user:', err);
      setError(err instanceof Error ? err.message : 'Failed to search user');
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleBrandToggle = useCallback((brand: string) => {
    setTempSelectedBrands(prev => {
      const isSelected = prev.includes(brand);
      return isSelected
        ? prev.filter(b => b !== brand)
        : [...prev, brand];
    });
  }, []);

  const handleSearch = useCallback(() => {
    setSelectedBrands(tempSelectedBrands);
    setCursorHistory([]); // Reset cursor history for new search
    if (tempSelectedBrands.length > 0) {
      // Reset to first page when starting a new search
      fetchTrends(tempSelectedBrands, DEFAULT_PAGE_SIZE);
    } else {
      // When no brands are selected, don't make an API call
      // The API will treat empty array as all brands, but we want to show nothing
      setSearchResults([]);
      setPagination(DEFAULT_PAGINATION);
    }
  }, [tempSelectedBrands, fetchTrends]);

  const handlePageChange = useCallback((cursor?: string) => {
    // If no cursor is provided, go back to first page
    if (!cursor) {
      setCursorHistory([]);
      if (selectedBrands.length > 0) {
        fetchTrends(selectedBrands, pagination.limit);
      } else if (searchResults.length > 0) {
        fetchTrends([], pagination.limit);
      }
      return;
    }

    // Allow pagination when we have selected brands OR when we're showing all brands (empty array)
    if (selectedBrands.length > 0) {
      fetchTrends(selectedBrands, pagination.limit, cursor, true);
    } else if (searchResults.length > 0) {
      // If no brands are selected but we have results, we're showing all brands
      fetchTrends([], pagination.limit, cursor, true);
    }
  }, [selectedBrands, searchResults.length, pagination.limit, fetchTrends]);

  const handleModalClose = useCallback(() => {
    // Don't commit temporary selections when modal is closed - only commit when user presses search
    // Reset temp selections to current committed selections to discard any uncommitted changes
    setTempSelectedBrands(selectedBrands);
  }, [selectedBrands]);

  const clearSelectedBrands = useCallback(() => {
    // Clear both temporary and committed selections, plus search results when called from modal
    setSelectedBrands([]);
    setTempSelectedBrands([]);
    setSearchResults([]);
    setPagination(DEFAULT_PAGINATION);
    setCursorHistory([]); // Reset cursor history
    // Reload all brands when clearing selections
    fetchTrends([], DEFAULT_PAGE_SIZE);
  }, [fetchTrends]);

  const clearAndCommit = useCallback(() => {
    // Clear selections and immediately commit them (for Clear All button)
    setSelectedBrands([]);
    setTempSelectedBrands([]);
    setSearchResults([]);
    setPagination(DEFAULT_PAGINATION);
    // Reload all brands when clearing selections
    fetchTrends([], DEFAULT_PAGE_SIZE);
  }, [fetchTrends]);

  const clearAllSelections = useCallback(() => {
    // Clear both temporary and committed selections
    setSelectedBrands([]);
    setTempSelectedBrands([]);
    setSearchResults([]);
    setPagination(DEFAULT_PAGINATION);
    // Reload all brands when clearing selections
    fetchTrends([], DEFAULT_PAGE_SIZE);
  }, [fetchTrends]);

  const resetTempSelections = useCallback(() => {
    setTempSelectedBrands(selectedBrands);
  }, [selectedBrands]);

  // Load all brands on page load
  useEffect(() => {
    let isMounted = true;
    
    const loadTrends = async () => {
      if (isMounted) {
        await fetchTrends([], DEFAULT_PAGE_SIZE);
      }
    };
    
    loadTrends();
    
    return () => {
      isMounted = false;
    };
  }, [fetchTrends]);

  return {
    styles,
    selectedBrands,
    tempSelectedBrands,
    handleBrandToggle,
    handleSearch,
    handlePageChange,
    handleModalClose,
    clearSelectedBrands,
    clearAndCommit,
    clearAllSelections,
    resetTempSelections,
    fetchTrends,
    isLoading,
    error,
    searchResults,
    pagination,
  };
}; 